import { compile } from "path-to-regexp"
import {
    type CombineRoutesWithQuery,
    type InternalRoute,
    type PathParamsObject,
    type QueryParamsObject,
    type ResolveParams,
    type Route,
} from "./types"

export class RoutePath<TRoute extends Route = Route> {
    route: TRoute

    private constructor(route: TRoute) {
        this.route = route
    }

    static create<TRoute extends Route>(route: TRoute): RoutePath<TRoute> {
        return new RoutePath(route)
    }

    /**
     * Creates a new RoutePath instance using the current route path as a route namespace.
     * This method allows for recursive route path building while maintaining type safety.
     *
     * @param subRoute - The sub-route to append to the current route
     * @returns A new RoutePath instance with the combined route
     */
    create<TSubRoute extends InternalRoute>(
        subRoute: TSubRoute
    ): RoutePath<CombineRoutesWithQuery<TRoute, TSubRoute>> {
        const combinedRoute = this.combineRoutes(this.route, subRoute) as CombineRoutesWithQuery<
            TRoute,
            TSubRoute
        >

        return new RoutePath(combinedRoute)
    }

    /**
     * Combines the current route path with a sub-route, ensuring proper route path joining
     */
    private combineRoutes<TSubRoute extends InternalRoute>(
        baseRoute: TRoute,
        subRoute: TSubRoute
    ): string {
        const [basePath, baseQuery] = this.splitPathAndQuery(baseRoute)
        const [subRoutePath, subRouteQuery] = this.splitPathAndQuery(subRoute)

        const combinedPath = `${basePath}${subRoutePath}`

        if (baseQuery && subRouteQuery) {
            return this.buildFinalPath(combinedPath, `${baseQuery}&${subRouteQuery}`)
        }

        if (baseQuery) {
            return this.buildFinalPath(combinedPath, baseQuery)
        }

        if (subRouteQuery) {
            return this.buildFinalPath(combinedPath, subRouteQuery)
        }

        return combinedPath
    }

    resolve(...resolveParams: ResolveParams<TRoute>): string {
        const [path, querySearch] = this.splitPathAndQuery(this.route)
        const [params] = resolveParams as [
            { pathParams?: PathParamsObject<TRoute>; queryParams?: QueryParamsObject<TRoute> }?,
        ]

        const { origin = "", relativePath } = this.splitOriginAndPath(path)
        const compiledPath = this.getCompiledPath(relativePath, params?.pathParams)

        if (!querySearch) {
            return origin + compiledPath
        }

        const processedQuery = this.processQueryParams(querySearch, params?.queryParams)

        return origin + this.buildFinalPath(compiledPath, processedQuery)
    }

    private getCompiledPath(
        path: string,
        pathParamValues: PathParamsObject<TRoute> | undefined
    ): string {
        if (pathParamValues) {
            return compile(path, { encode: false })(pathParamValues)
        }

        return path
    }

    private processQueryParams(
        querySearch: string,
        queryParamValues: QueryParamsObject<TRoute> | undefined
    ): string {
        if (!querySearch || !queryParamValues) {
            return ""
        }

        const searchParams = new URLSearchParams()

        querySearch.split("&").forEach((segment) => {
            const [urlKey, paramName] = segment.split("=:")
            if (!urlKey || !paramName) return

            const value = queryParamValues[paramName as keyof QueryParamsObject<TRoute>]
            if (!value) return

            searchParams.set(urlKey, value)
        })

        return searchParams.toString()
    }

    private buildFinalPath(path: string, query: string): string {
        if (query.length > 0) {
            return `${path}?${query}`
        }

        return path
    }

    private splitOriginAndPath(path: string) {
        const match = path.match(/^([a-z][a-z0-9+\-.]*:\/\/[^/]*)(.*)$/i)

        if (!match) {
            return { relativePath: path }
        }

        return { origin: match[1], relativePath: match[2] }
    }

    private splitPathAndQuery(path: string) {
        return path.split("?") as [string, string?]
    }
}
